import { useState } from 'preact/hooks';
import { Icons } from './icons/icon-set';
import { useClickOutside } from './use-click-outside';
import type { Snippet } from '../data/snippets';
import type { HistoryEntry } from '../app-types';

interface SnippetsMenuProps {
  snippets: Snippet[];
  onPick: (s: Snippet) => void;
}

export function SnippetsMenu({ snippets, onPick }: SnippetsMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  return (
    <div class="snippets" style={{ position: 'relative' }} ref={ref}>
      <button class="chip" aria-label="Examples" onClick={() => setOpen((o) => !o)}>
        <Icons.doc size={14} /> <span class="chip-label">Examples</span>{' '}
        <Icons.chevron size={13} class="chip-caret" style={{ opacity: 0.5 }} />
      </button>
      {open && (
        <>
          {/* Mobile scrim: the panel centers as a modal there, so a backdrop
              catches the dismiss tap instead of it landing on a stray button. */}
          <div class="popover-backdrop" onClick={() => setOpen(false)} />
          <div class="popover">
            <div class="popover-head">Sample snippets</div>
            <div class="snip-list">
            {snippets.map((s) => (
              <div
                key={s.id}
                class="snip-item"
                onClick={() => {
                  onPick(s);
                  setOpen(false);
                }}
              >
                <span class="snip-emoji">{s.emoji}</span>
                <div class="snip-main">
                  <div class="snip-title">{s.title}</div>
                  <div class="snip-desc">{s.desc}</div>
                </div>
              </div>
            ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface HistoryMenuProps {
  items: HistoryEntry[];
  onPick: (h: HistoryEntry) => void;
  onClear: () => void;
}

export function HistoryMenu({ items, onPick, onClear }: HistoryMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  return (
    <div class="history" style={{ position: 'relative' }} ref={ref}>
      <button class="chip" aria-label="History" onClick={() => setOpen((o) => !o)}>
        <Icons.history size={14} /> <span class="chip-label">History</span>
      </button>
      {open && (
        <>
          <div class="popover-backdrop" onClick={() => setOpen(false)} />
          <div class="popover">
          <div class="popover-head">
            <span>Saved runs</span>
            {items.length > 0 && (
              <span
                style={{ cursor: 'pointer', textTransform: 'none', fontWeight: 700, color: 'var(--text-faint)' }}
                onClick={onClear}
              >
                Clear
              </span>
            )}
          </div>
          {items.length === 0 ? (
            <div class="hist-empty">
              No runs yet.
              <br />
              Each run will be saved here.
            </div>
          ) : (
            <div class="hist-list">
              {items.map((h) => (
                <div
                  key={h.id}
                  class="hist-item"
                  onClick={() => {
                    onPick(h);
                    setOpen(false);
                  }}
                >
                  <div class="hist-icon">
                    <Icons.terminal size={17} />
                  </div>
                  <div class="hist-main">
                    <div class="hist-title">{h.title}</div>
                    <div class="hist-meta">
                      <span class="hist-vers">{h.versions.join(' · ')}</span>
                      <span>{h.when}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </>
      )}
    </div>
  );
}
